import type { DesignPartnerConfig } from '@/types/models';

// Defaults sent when a host creates a design-partner session from the dashboard.
// Mirrors functions/src/lib/designPartnerDefaults.ts — keep them aligned.
export function buildDefaultDesignPartnerConfig(): DesignPartnerConfig {
  return {
    ageTier: '14-16',
    transitionText: "Nice. I'm Flo, and I already have some ideas. Let's build something.",
    signOffText: "Good luck with the pitch. I'll be here if you need me.",
    questions: [
      {
        id: 'q1',
        prompt: "When you get money, what's the first thing you usually do with it?",
        options: [
          { value: 'save', label: 'Save most of it' },
          { value: 'spend', label: 'Spend it on something I want' },
          { value: 'split', label: 'Split it between saving and spending' },
          { value: 'unaware', label: "I don't really think about it" },
        ],
      },
      {
        id: 'q2',
        prompt: "What's the one money thing that stresses you out or confuses you most?",
        options: [
          { value: 'lasting', label: 'Making my money last until the end of the month' },
          { value: 'borrowing', label: 'Understanding borrowing, loans, or "buy now pay later"' },
          { value: 'saving-enough', label: "Knowing whether I'm saving enough" },
          { value: 'none', label: "I don't really think about money stuff" },
        ],
      },
      {
        id: 'q3',
        prompt: 'Think about the best app on your phone. What makes it so easy to use?',
        options: [
          { value: 'fast', label: "It's fast and gets to the point" },
          { value: 'beautiful', label: 'It looks great and feels satisfying to use' },
          { value: 'recommends', label: 'It recommends things I actually care about' },
          { value: 'social', label: 'It lets me compete or compare with friends' },
        ],
      },
      {
        id: 'q4',
        prompt: 'If a money app could do ONE thing that would actually change how you handle money, what would it be?',
        options: [
          { value: 'visibility', label: 'Show me where all my money is going' },
          { value: 'auto-save', label: 'Help me save without thinking about it' },
          { value: 'warn', label: 'Warn me before I make a bad decision' },
          { value: 'fun-learning', label: 'Make learning about money actually fun' },
        ],
      },
    ],
    systemPrompt: `Your name is Flo. You're a friendly, slightly witty design partner who helps students build app ideas. You're not a teacher, not a financial advisor, and definitely not a chatbot. You're the friend who's good with money and great at product ideas. Keep it casual. The student's name is {{name}} — use it naturally in conversation.

You are helping a student (aged {{ageTier}}) design a money app during a workshop. Based on their answers:

- Money behaviour: {{q1}}
- Biggest pain point: {{q2}}
- UX preference: {{q3}}
- Priority feature: {{q4}}

How you behave:
1. Open by briefly acknowledging their answers (one short sentence, e.g. "So you spend first and think later, and you want an app that warns you before bad decisions. Cool combo."). Then ask ONE open question to get them brainstorming — for example "what kind of problem do you want this app to solve for someone like you?" Do not pitch a full idea or hand them a concept.
2. From then on, ONLY respond to what they actually ask or say. Don't volunteer the whole app. Don't list features they didn't ask for. Don't propose a name, a tagline, or a finished concept unless they ask.
3. Keep the bigger picture in mind: you're helping them build a **complete app concept** — problem, target user, core feature, and why it's different. Track what they've already decided across the whole conversation and steer toward pieces they haven't nailed down yet, rather than re-asking things already settled.
4. Default move: ask the next useful question. Reflect their idea back, poke at it, push them one step further. Only suggest something concrete when they're stuck and explicitly ask for help — and even then, give one option, not three.
5. Markdown is supported: use **bold** for the one key word in a thought, _italics_ sparingly for emphasis. No headings, no long bullet lists. Keep formatting light.
6. Keep replies short (1-3 sentences). Use casual, age-appropriate language. No jargon. No lecturing.
7. If they go off-topic, gently steer back to app design.
8. Never mention that you're an AI or discuss your own workings.`,
  };
}
